import { Button, Col, Form } from "antd";
import styled from "styled-components";
import { useCallback, useMemo, useState } from "react";
import { IconBanSolid } from "../../../icon";
import FormItemString from "./form-items/form-item-string";
import FormItemBackendEnum from "./form-items/form-item-backend-enum";
import FormItemDate from "./form-items/form-item-date";
import FormItemNumber from "./form-items/form-item-number";
import FormItemCheckbox from "./form-items/form-item-checkbox";
import FormItemCurrency from "./form-items/form-item-currency";
import FormItemEnum from "./form-items/form-item-enum";
import FormItemStaticLink from "./form-items/form-item-static-link";
import FormItemDaysOfWeek from "./form-items/form-item-days-of-week";
import FormItemRef from "./form-items/form-item-ref";
import FormItemDatetime from "./form-items/form-item-datetime";
import FormItemMonth from "./form-items/form-item-month";
import FormItemUser from "./form-items/form-item-user";
import FormItemButton from "./form-items/form-item-button";
import FormItemText from "./form-items/form-item-text";

export const REGEX_EMAIL = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

export const createRegexRule = (regex: RegExp, message) => {
  return () => ({
      validator(_, value) {
        if (!value || value.match(regex)) {
          return Promise.resolve();
        }
        return Promise.reject(new Error(message));
      },
    })
}

export const createRequiredRule = (message) => {
  return () => ({
      validator(_, value) {
        if (value) {
          return Promise.resolve();
        }
        return Promise.reject(new Error(message));
      },
    })
}

export interface FormItemPropsType {
  fieldName: string;
  label: string;
  type?: "string" | "text" | "enum" | "backendEnum" | "date" | "datetime" | "month" | "check" | "number" | "currency" | "ref" | "base64Img" | "staticLink" | "daysOfWeek" | "empty" | "user" | "button";
  typeData?: any;
  rules?: any[];
  readOnly?: boolean;
  disabled?: boolean;
  field?: any;
  spans?: any;
  value?: any;
}

const DropZone = styled.div`
  width: 100%;
  border: 3px dashed gray;

  &.dragging {
    border: 3px dashed #AAAAAA;
  }

  text-align: center;
  background-color: white;

  input {
    opacity: 0;
    width: 100%;
    position: absolute;
    height: 100%;
    cursor: pointer;
  }

  .imageWrapper {
    position: relative;
    pointer-events: none;

    button {
      position: absolute;
      right: 0;
      top: 0;
       pointer-events: auto;
    }

    img {
      width: 100%;
    }
  }
`

const toBase64 = file => new Promise((resolve, reject) => {
  if(!file) {
    return null;
  }

  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
});

function Base64ImageSelector({ value, onChange, readOnly }: any) {
  const [dragging, setDragging] = useState<boolean>(false);

  const onChangeInternal = useCallback(async evt => {
    const file = evt.target.files[0];
    const base64 = await toBase64(file);
    console.log("Base 64", base64);
    onChange(base64);
  }, [onChange])

  return <DropZone className={`${dragging ? "dragging" : ""}`} onDragOver={() => setDragging(true)} onDragLeave={() => setDragging(false)}><input type="file" accept=".jpg, .png, .jpeg, .svg" onChange={onChangeInternal} />
    { !value && (dragging ? <>Jetzt<br/>loslassen!</> : <>Hier klicken oder<br/>Bild hierher ziehen</>) }
    { value && <div className="imageWrapper"><Button danger icon={<IconBanSolid />} onClick={evt => { onChange(null); evt.stopPropagation(); }} /><img src={value} alt="" /></div> }
  </DropZone>
}

function FormItem({ fieldName, label, type = "string", readOnly = false, disabled = false, typeData, rules, field, spans, value }: FormItemPropsType) {
  const combinedFieldName = useMemo(() => fieldName ? [...(field ? [field.name] : []), fieldName] : undefined, [field, fieldName])
  if(!spans) {
    spans = {
      sm: 20,
      md: 20,
      lg: 10,
      xl: 8,
      xxl: 5
    }
  }
  return (
    <Col {...spans}>
      <Form.Item 
        {...field}
        labelCol={{ span: 10 }}
        wrapperCol={{ span: 14 }}
        name={combinedFieldName}
        label={label}
        rules={rules}
      >
        { type === 'string' && <FormItemString readOnly={readOnly} disabled={disabled} label={label} /> }
        { type === 'text' && <FormItemText readOnly={readOnly} disabled={disabled} label={label} /> }
        { type === 'number' && <FormItemNumber readOnly={readOnly} disabled={disabled} min={typeData?.min ?? 0} max={typeData?.max} addonAfter={typeData?.addonAfter} /> }
        { type === 'enum' && <FormItemEnum disabled={disabled} readOnly={readOnly} typeData={typeData} placeholder={label} /> }
        { type === 'backendEnum' && <FormItemBackendEnum readOnly={readOnly} disabled={disabled} typeData={typeData} /> }
        { type === 'date' && <FormItemDate readOnly={readOnly} disabled={disabled} label={label} typeData={typeData} /> }
        { type === 'datetime' && <FormItemDatetime readOnly={readOnly} disabled={disabled} label={label} typeData={typeData} /> }
        { type === 'month' && <FormItemMonth readOnly={readOnly} disabeld={disabled} label={label} typeData={typeData} />}
        { type === 'check' && <FormItemCheckbox readOnly={readOnly} disabled={disabled} /> }
        { type === 'currency' && <FormItemCurrency readOnly={readOnly} disabled={disabled}  /> }
        { type === 'ref' && <FormItemRef readOnly={readOnly} disabled={disabled} typeData={typeData} />}
        { type === 'base64Img' && <Base64ImageSelector readonly={readOnly} />}
        { type === 'staticLink' && <FormItemStaticLink typeData={typeData} />}
        { type === 'daysOfWeek' && <FormItemDaysOfWeek readOnly={readOnly} />}
        { type === 'user' && <FormItemUser readOnly={readOnly} disabled={disabled} typeData={typeData} />}
        { type === 'button' && <FormItemButton readOnly={readOnly} disabled={disabled} typeData={typeData} />}
      </Form.Item>
    </Col>
  )
}

export default FormItem;